import { useId, useRef, type ReactNode, type RefObject } from 'react';
import { useDismissibleLayer } from '../useDismissibleLayer';
import { useRoute } from '../router';
import { ChevronEndIcon, ChevronStartIcon } from './icons';

/**
 * The app shell's side navigation, in both modes: pushing the content across in its own grid column,
 * or floating over it as a modal surface. Collapsed is a 56px icon column either way — the mode
 * describes only what EXPANDED means, which is why it costs one attribute rather than two rails.
 *
 * WHAT THIS COMPONENT DOES NOT DO, and why.
 *
 * It does not read the session. A nav item may be permission-gated, but `can()` only exists inside a
 * component under `AuthProvider`, and a rail that reached for it could then only ever render there —
 * including in its own tests, which would have to boot an auth provider and stub `fetch` to assert
 * that a chevron points the right way. Filtering is `visibleItems()` below: a pure function the
 * shell calls with its own `can`. The rail receives the items a person may actually use.
 *
 * It does not decide expanded-vs-collapsed. `collapsed` is a prop and the shell owns it, because the
 * state has to survive a reload, has to be written before first paint to avoid a layout jump, and in
 * push mode is read by module CSS through an attribute on the root. One owner, in the shell.
 *
 * It does not switch between push and overlay by measuring the viewport. jsdom has no `matchMedia`
 * and packages/ui has no vitest setup file, so a component that listened to a media query would take
 * down every test that mounts the shell — this has happened before, and the workaround is recorded
 * in ThemeToggle.tsx. JS owns open/closed; CSS owns what open means at a given width.
 */

interface NavItemBase {
  /**
   * The visible text — and the accessible name when collapsed.
   *
   * It stays in the DOM in both states and is hidden VISUALLY by CSS, never removed and never
   * swapped for an `aria-label`. A collapsed rail therefore announces exactly what an expanded one
   * announces, and the two states cannot drift apart in wording.
   */
  label: string;
  icon: ReactNode;
  /**
   * Hide this item unless the session holds the permission. A plain string, not a union type: `can`
   * takes `string` (AuthProvider.tsx) and no `PermissionId` type exists in either repo.
   */
  permission?: string;
  /** One figure that survives the collapse, as a dot on the icon. */
  badge?: number | string;
}

/** A destination. */
export interface NavLink extends NavItemBase {
  path: string;
  /** Also highlight for paths BELOW this one — claro's `/templates/new` lighting `/templates`. */
  matchPrefix?: boolean;
}

/**
 * An act, not a place.
 *
 * The plan's sketch had links only, which does not survive contact with the four shared items:
 * logout is one of them and it is not a route. Modelled as a union rather than an optional `path`
 * so a caller cannot supply neither, or both.
 */
export interface NavAction extends NavItemBase {
  onSelect: () => void;
}

/**
 * A screen this module intends to have and does not have yet.
 *
 * A deliberate signpost, not a placeholder to forget about: the rail is where a product says what
 * it is going to be, and a module with one screen has nothing to say without this. It has neither a
 * `path` nor an `onSelect` — which is the point, and is why it is a variant rather than a flag. A
 * "disabled link" is still a link: it typechecks with a destination, tempts a reader to press it,
 * and `aria-disabled` on an anchor does not stop activation.
 *
 * Give it a `path` and delete `disabled` when the screen exists. Nothing else has to change.
 */
export interface NavSoon extends NavItemBase {
  disabled: true;
}

export type NavItem = NavLink | NavAction | NavSoon;

export function isNavLink(item: NavItem): item is NavLink {
  return 'path' in item;
}

/** A signposted screen that does not exist yet — neither a destination nor an act. */
export function isNavSoon(item: NavItem): item is NavSoon {
  return 'disabled' in item && item.disabled === true;
}

/** The items this caller may use. The shell supplies `can`; see the note at the top of the file. */
export function visibleItems(items: readonly NavItem[], can: (permission: string) => boolean): NavItem[] {
  return items.filter((item) => item.permission === undefined || can(item.permission));
}

/**
 * Is `item` the screen currently shown?
 *
 * Exact by default. `matchPrefix` extends it to descendants and is the reason a builder screen does
 * not blank the rail — but only on a real segment boundary, so `/templates` never lights for
 * `/templates-archive`.
 */
export function isActive(item: NavItem, path: string): boolean {
  if (!isNavLink(item)) return false;
  if (path === item.path) return true;
  if (item.matchPrefix !== true) return false;
  // Root is exact-only. Prefix-matching '/' would light the home item on every screen in the app,
  // which is not a highlight, it is a permanent one.
  if (item.path === '/') return false;
  return path.startsWith(item.path.replace(/\/$/, '') + '/');
}

export interface SideRailProps {
  /** The module's own destinations, at the top. */
  items?: readonly NavItem[];
  /** The platform's items — organization, account, logout — pushed to the bottom. */
  footer?: readonly NavItem[];
  /**
   * The brand GLYPH — a mark, or the org's white-label logo. Shown in both states.
   *
   * Split from `brandName` rather than taken as one lockup because at 56px the two cannot both fit,
   * and a single node leaves the rail unable to tell which half to drop: rendered as one, the word
   * is clipped mid-glyph and the collapsed rail is headed by half a Hebrew letter. Measured, in
   * Chrome, before this was split.
   */
  brand?: ReactNode;
  /** The product's name. Hidden from EYES when collapsed, exactly as an item label is. */
  brandName?: string;
  /**
   * Where the lockup goes — '/' in every module today.
   *
   * The rail builds the anchor rather than taking one, so the NAME sits inside the link too. Passing
   * a ready-made `<a>` as `brand` would put only the glyph in it, and the clickable target in the
   * expanded rail would be a 22px square beside a word that looks equally clickable and is not.
   */
  brandHref?: string;
  collapsed?: boolean;
  onToggle?: () => void;
  /** Controls that are not nav items, e.g. the theme toggle. Sits above the collapse control. */
  children?: ReactNode;
  /** Accessible name for the landmark. There is one nav per shell, so it rarely needs overriding. */
  label?: string;
  /**
   * How the EXPANDED rail behaves. Collapsed is identical either way — a 56px icon column.
   *
   * `push` gives the expanded rail its own grid column and the content moves across.
   * `overlay` floats it over the content instead, with a scrim, and the grid never reflows.
   *
   * The choice is per module and it is a real one: push is calmer and costs the content permanent
   * width; overlay costs nothing until opened and is a modal surface, with everything that implies.
   * A module whose main screen owns the edge — a search with its own facet sidebar — wants overlay.
   */
  railMode?: 'push' | 'overlay';
}

export function SideRail({
  items = [],
  footer = [],
  brand,
  brandName,
  brandHref,
  collapsed = false,
  onToggle,
  children,
  label = 'ניווט ראשי',
  railMode = 'push',
}: SideRailProps) {
  const { path, navigate } = useRoute();
  const railId = useId();
  const surface = useRef<HTMLElement | null>(null);

  /*
    An expanded OVERLAY rail is a modal surface and has to behave like one — focus in, focus
    trapped, focus restored, Escape closes. An expanded PUSH rail is not: it sits in the layout
    beside the content, nothing is covered, and trapping focus in it would be wrong.

    So the layer is active on exactly one combination, and `useDismissibleLayer` is inert otherwise.
  */
  /*
    `onToggle !== undefined` is part of the condition, not an oversight. Without it,
    `<SideRail railMode="overlay" />` with no handler renders a full-viewport scrim over a
    focus-trapped rail whose only dismissal — Escape — calls a callback that does not exist. The
    page is locked with no way out, and it typechecks, because `onToggle` is optional. A layer with
    no exit is not a layer; it degrades to a plain rail instead.
  */
  const modal = railMode === 'overlay' && !collapsed && onToggle !== undefined;

  /*
    Choosing a destination closes a MODAL rail, and only a modal one.

    Without this the route changes underneath an overlay that stays open, aria-modal keeps hiding
    the page the reader just asked for, and focus stays inside the nav — they have to dismiss a
    dialog they did not know they were still in. Guarded on `modal` because the same call in push
    mode would collapse the rail on every click in all four shipping apps, which is not what anyone
    asks for by clicking a link.
  */
  const leave = modal ? onToggle : undefined;

  useDismissibleLayer({
    ref: surface,
    active: modal,
    onDismiss: () => onToggle?.(),
  });

  return (
    <>
      {/* The scrim is a SIBLING, not a wrapper. Wrapping the rail would put a new element between
          it and the shell's grid, and the rail's own column placement is what push mode is made of.
          It renders only while the overlay is actually open, so nothing is layered over the content
          in push mode or while collapsed. */}
      {modal && <div className="huaca-rail-scrim" data-testid="rail-scrim" onClick={onToggle} />}
      {/*
        THE DIALOG IS A WRAPPER, and the <nav> stays a <nav>.

        Putting `role="dialog"` on the nav itself was the obvious shape and is wrong: an explicit
        role REPLACES the implicit one, so the element stops being a navigation landmark — while
        `aria-modal` prunes everything outside it at the same moment. A screen-reader user opening
        the navigation would find the landmark list EMPTY, at the exact instant the navigation is
        the only thing on screen. `dialog` is also not an allowed role on `nav` per ARIA-in-HTML.

        The wrapper is also what the overlay is positioned by, so the fixed box and the modal
        semantics are one element and cannot drift apart.
      */}
      <Wrap wrapped={railMode === 'overlay'} modal={modal} label={label} surface={surface}>
      <nav
        id={railId}
        className="huaca-rail"
        /*
          An ATTRIBUTE, not a class, and present-or-absent rather than "true"/"false". Module CSS keys
          off rail state (a push rail makes every viewport-width media query measure the wrong box), so
          the state has to be expressible as a plain attribute selector in a stylesheet that knows
          nothing about this component.
        */
        data-collapsed={collapsed ? '' : undefined}
        data-rail-mode={railMode}
        data-testid="side-rail"
        aria-label={label}
      >
        {(brand !== undefined || brandName !== undefined) &&
          /*
            The lockup stays MOUNTED when collapsed and is narrowed by CSS instead.
            `me.org.logoUrl` is a signed URL with a five-minute TTL, minted only when /auth/me is
            called and refreshed by nothing. Unmounting it on collapse and remounting on expand
            re-requests it, so in any session older than five minutes a paying tenant's white-label
            logo would break the first time somebody collapsed the rail.
          */
          (brandHref === undefined ? (
            <div className="huaca-rail__brand">
              {brand}
              {brandName !== undefined && <span className="huaca-rail__brandname">{brandName}</span>}
            </div>
          ) : (
            <a
              className="huaca-rail__brand"
              href={brandHref}
              data-testid="rail-brand"
              onClick={(e) => {
                if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                e.preventDefault();
                navigate(brandHref);
                // The lockup navigates through the same path as an item and had the identical
                // problem: home behind a modal rail that stayed open over it.
                leave?.();
              }}
            >
              {brand}
              {brandName !== undefined && <span className="huaca-rail__brandname">{brandName}</span>}
            </a>
          ))}

        {items.length > 0 && (
          <ul className="huaca-rail__list">
            {items.map((item) => (
              <RailItem key={keyOf(item)} item={item} path={path} navigate={navigate} collapsed={collapsed} onLeave={leave} />
            ))}
          </ul>
        )}

        <div className="huaca-rail__foot">
          {footer.length > 0 && (
            <ul className="huaca-rail__list">
              {footer.map((item) => (
                <RailItem key={keyOf(item)} item={item} path={path} navigate={navigate} collapsed={collapsed} onLeave={leave} />
              ))}
            </ul>
          )}
          {children}
          {onToggle !== undefined && (
            <button
              type="button"
              className="huaca-rail__item huaca-rail__toggle"
              /*
                The control sits INSIDE the region it controls. That is deliberate: in the collapsed
                rail a 24px mark and a 28px control do not both fit across a 56px header, and the one
                that has to stay reachable at every width is this one. `aria-controls` still names the
                region so the relationship survives the placement.
              */
              aria-expanded={!collapsed}
              aria-controls={railId}
              data-testid="rail-toggle"
              onClick={onToggle}
            >
              <span className="huaca-rail__icon">
                {/*
                  THE GLYPH IS THE STATE. A hamburger says "there is a menu" and says it identically
                  whether the menu is open or shut, which on a control that is only ever a toggle is
                  the one thing it needed to say.

                  The chevron points where the rail is about to TRAVEL: expanded, it points at the
                  rail's own edge, because pressing shrinks it that way; collapsed, it points into
                  the content, because pressing grows it there. Both glyphs carry `huaca-icon--dir`,
                  so under dir="rtl" — the direction this product ships in, rail on the right — they
                  mirror and keep meaning the same two things.
                */}
                {collapsed ? <ChevronEndIcon /> : <ChevronStartIcon />}
              </span>
              <span className="huaca-rail__label">{collapsed ? 'הרחבת התפריט' : 'כיווץ התפריט'}</span>
            </button>
          )}
        </div>
      </nav>
      </Wrap>
    </>
  );
}

/**
 * The overlay's surface. Present whenever the rail is in overlay MODE — not only while it is open.
 *
 * Keyed on the mode rather than on `modal` because the mode is a static prop and `modal` is not:
 * swapping between `<div><nav/></div>` and a bare `<nav/>` changes the element type at that
 * position, so React unmounts the entire rail and mounts a fresh one every time the overlay closes.
 * The consequence is not theoretical — the toggle button the reader pressed is a NEW DOM node
 * afterwards, `document.contains(opener)` is false, focus restore correctly declines to chase a
 * detached element, and focus lands on <body>. Which is the precise failure the restore exists to
 * prevent, arrived at by building the fix for a different one. Caught by the test below.
 *
 * ARIA moves; the element does not. `role` and `aria-modal` appear only while it is genuinely
 * modal, and go when it closes.
 */
function Wrap({
  wrapped,
  modal,
  label,
  surface,
  children,
}: {
  wrapped: boolean;
  modal: boolean;
  label: string;
  surface: RefObject<HTMLElement | null>;
  children: ReactNode;
}) {
  if (!wrapped) return <>{children}</>;
  return (
    <div
      className="huaca-rail-dialog"
      ref={surface as RefObject<HTMLDivElement | null>}
      /* Focusable itself, so the trap has somewhere to hold focus before any item is reachable. */
      {...(modal && { tabIndex: -1, role: 'dialog', 'aria-modal': true, 'aria-label': label })}
      data-testid="rail-dialog"
    >
      {children}
    </div>
  );
}

/** Stable across renders and unique within a rail: a destination by path, an act by its label. */
function keyOf(item: NavItem): string {
  return isNavLink(item) ? `path:${item.path}` : `act:${item.label}`;
}

function RailItem({
  item,
  path,
  navigate,
  collapsed,
  onLeave,
}: {
  item: NavItem;
  path: string;
  navigate: (to: string) => void;
  collapsed: boolean;
  /** Called after a destination is chosen — set only while the rail is modal, so it can close. */
  onLeave?: (() => void) | undefined;
}) {
  const active = isActive(item, path);
  const body = (
    <>
      <span className="huaca-rail__icon">{item.icon}</span>
      <span className="huaca-rail__label">{item.label}</span>
      {item.badge !== undefined && item.badge !== '' && (
        <span className="huaca-rail__badge" data-testid="rail-badge">
          {item.badge}
        </span>
      )}
    </>
  );

  // A native tooltip for the pointer user only while the text is not on screen. Never the
  // accessible name — the label element is that, in both states.
  const title = collapsed ? item.label : undefined;

  if (isNavSoon(item)) {
    /*
      Not an <a> and not a <button>. Both are promises — a keyboard user tabs to them expecting
      something, and `aria-disabled` on an anchor does not stop it being activated. A <span> is
      honest: it is announced with its label and its unavailability, it is skipped by Tab, and there
      is nothing to press.
    */
    return (
      <li>
        <span className="huaca-rail__item" aria-disabled="true" data-testid={`rail-soon-${item.label}`}>
          {body}
        </span>
      </li>
    );
  }

  return (
    <li>
      {isNavLink(item) ? (
        <a
          className="huaca-rail__item"
          href={item.path}
          // `page`, not `true`: this marks WHICH SCREEN is showing, and a screen reader announces
          // "current page" rather than the bare "current" that `true` produces.
          aria-current={active ? 'page' : undefined}
          {...(title !== undefined && { title })}
          data-testid={`rail-item-${item.path}`}
          onClick={(e) => {
            // Modified clicks belong to the browser: a middle-click or ⌘-click on a nav item must
            // still open a tab. Only a plain left click is ours to intercept.
            if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            e.preventDefault();
            navigate(item.path);
            onLeave?.();
          }}
        >
          {body}
        </a>
      ) : (
        <button
          type="button"
          className="huaca-rail__item"
          {...(title !== undefined && { title })}
          data-testid={`rail-action-${item.label}`}
          onClick={item.onSelect}
        >
          {body}
        </button>
      )}
    </li>
  );
}
